import { getScaleFromIncrement, toBig, toBigWithDefault, type Quote, type SideEnum } from '@talos/kyoko';
import type Big from 'big.js';

/** Given a fixing price, will return the new traded price and traded amount rounded in favor of the taker (us) */
export function getPricingDetailsFromFixing({
  fixingPrice,
  priceIncrement,
  amountIncrement,
  quote,
}: {
  fixingPrice: string | undefined;
  priceIncrement: string | undefined;
  amountIncrement: string | undefined;
  quote: Pick<Quote, 'TradedSide' | 'TradedQty' | 'PricingReference'> | undefined;
}) {
  if (!priceIncrement || !quote?.TradedSide) {
    return undefined;
  }

  const fixing = toBigWithDefault(fixingPrice, 0);

  const spread = toBig(quote.PricingReference);
  if (!spread) {
    return undefined;
  }

  const pxUnrounded = fixing.times(spread.plus(1));
  if (!pxUnrounded) {
    return undefined;
  }

  const priceScale = getScaleFromIncrement(priceIncrement);
  const px = roundPxOrAmtInMyFavor(pxUnrounded, priceScale, quote.TradedSide);

  const amtUnrounded = toBig(quote.TradedQty)?.times(pxUnrounded);
  if (!amtUnrounded) {
    return undefined;
  }

  const amountScale = getScaleFromIncrement(amountIncrement);
  const amt = roundPxOrAmtInMyFavor(amtUnrounded, amountScale, quote.TradedSide);

  return { spread, px, amt, bps: spread.times(10_000) };
}

/**
 * For Buy orders, it is favorable if the price or amount is lower (rounding down).
 * For Sell orders, it is favorable if the price or amount is higher (rounding up).
 */
export function roundPxOrAmtInMyFavor(value: Big, scale: number, side: SideEnum): Big {
  return side === 'Buy' ? value.round(scale, 0) : value.round(scale, 3);
}
